import { useAppSelector } from "@app/store";
import { Block } from "@components/block";
import { Breadcrumbs } from "@components/breadcrumbs";
import { Button } from "@components/button";
import { Card } from "@components/card";
import { DevicesSelect } from "@components/devices-select";
import { errorMessage } from "@components/forms/error";
import {
  cyrillicWithSpacesRegex,
  digitsRegex,
  errorCodeFieldRegex,
  phoneRegex,
  timeRegex,
} from "@components/forms/regexp";
import { sanitizePhoneNumber, sanitizeValue } from "@components/forms/utils";
import { Input } from "@components/input";
import { FlexLoader, GlobalLoader } from "@components/loader";
import { CustomSelect } from "@components/select";
import { useRequisites, useRequisitesDispatch } from "@features/requisite";
import {
  DetailRequisite,
  RequisiteType,
  UpdateRequisiteForm,
} from "@features/requisite/api";
import { yupResolver } from "@hookform/resolvers/yup";
import { JSONFetchClientError } from "@request/client";
import classNames from "classnames";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import styles from "./detail-edit.module.scss";
import { useAuth } from "@features/auth";
import Checkbox from "@components/checkbox";

const schema = yup.object().shape({
  type: yup.string().required(errorMessage.required).trim(),
  phone_number: yup
    .string()
    .trim()
    .required(errorMessage.required)
    .transform(sanitizePhoneNumber)
    .matches(phoneRegex, errorMessage.invalidPhoneNumber),
  account_number: yup.string().when("type", {
    is: (val: RequisiteType) => "ACCOUNT" == val,
    then: (schema) =>
      schema
        .required(errorMessage.required)
        .matches(digitsRegex, errorMessage.onlyNumbers)
        .trim(),
    otherwise: (schema) => schema.notRequired(),
  }),
  card_number: yup
    .string()
    .required(errorMessage.required)
    .matches(digitsRegex, errorMessage.onlyNumbers)
    .min(16, (params) => errorMessage.min(params.min))
    .trim(),
  device_id: yup.string().when("$allowRequisitesWithoutDevice", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required(errorMessage.required).trim(),
  }),
  full_name: yup
    .string()
    .required(errorMessage.required)
    .min(2, (params) => errorMessage.min(params.min))
    .max(100, (params) => errorMessage.min(params.max))
    .matches(cyrillicWithSpacesRegex, errorMessage.onlyCyrillicWithSpaces)
    .trim(),
});

const farmSchema = schema.concat(
  yup.object().shape({
    max_amount_per_period: yup
      .number()
      .transform(sanitizeValue)
      .required(errorMessage.required)
      .positive(errorMessage.onlyPositive),
    start_time: yup
      .string()
      .required(errorMessage.required)
      .matches(timeRegex, errorMessage.time)
      .trim(),
    end_time: yup
      .string()
      .required(errorMessage.required)
      .matches(timeRegex, errorMessage.time)
      .trim(),
    max_transaction: yup
      .number()
      .transform(sanitizeValue)
      .required(errorMessage.required)
      .positive(errorMessage.onlyPositive),
    min_transaction: yup
      .number()
      .transform(sanitizeValue)
      .required(errorMessage.required)
      .positive(errorMessage.onlyPositive),
    min_interval: yup
      .number()
      .transform(sanitizeValue)
      .required(errorMessage.required)
      .positive(errorMessage.onlyPositive),
    transaction_count: yup
      .number()
      .transform(sanitizeValue)
      .required(errorMessage.required)
      .positive(errorMessage.onlyPositive),
    intrabank_only: yup.boolean(),
    // .required(),
  })
);

type UpdateRequisite = UpdateRequisiteForm & {
  timeVal: string;
};

export const DetailEditPage = () => {
  const { detailId } = useParams();

  const { state: requisiteState, getRequisite } = useRequisites();

  const requisite = requisiteState.requisite;

  useEffect(() => {
    if (detailId) {
      getRequisite(detailId);
    }
  }, [detailId, getRequisite]);

  if (!requisite) {
    return <FlexLoader />;
  }

  return <PageContent requisite={requisite} />;
};

type PageContentProps = {
  requisite: DetailRequisite;
};

const PageContent = ({ requisite }: PageContentProps) => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const allowFarmCreation = useAppSelector(
    (state) => state.auth.allowFarmCreation
  );
  const allowRequisitesWithoutDevice = useAppSelector(
    (state) => state.auth.allowRequisitesWithoutDevice
  );
  const location = useLocation();

  const onEndEdit = useCallback(
    () => navigate(`${location.pathname}/../`),
    [location.pathname, navigate]
  );

  const number = useMemo(() => {
    switch (requisite.type) {
      case "CARD":
        return requisite.card_number;
      case "ACCOUNT":
        return requisite.account_number;
      case "SBP":
      default:
        return requisite.phone_number;
    }
  }, [
    requisite.account_number,
    requisite.card_number,
    requisite.phone_number,
    requisite.type,
  ]);

  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
    // watch,
    // setValue,
  } = useForm<UpdateRequisite>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    resolver: yupResolver(allowFarmCreation ? farmSchema : schema),
    context: { allowRequisitesWithoutDevice },
    defaultValues: {
      end_time: requisite.end_time,
      max_amount_per_period: requisite.max_amount_per_period,
      start_time: requisite.start_time,
      type: requisite.type,
      account_number: requisite.account_number,
      card_number: requisite.card_number,
      phone_number: requisite.phone_number,
      device_id: requisite.device_id,
      full_name: requisite.full_name,
      max_transaction: requisite.max_transaction,
      min_transaction: requisite.min_transaction,
      min_interval: requisite.min_interval,
      transaction_count: requisite.transaction_count,
      intrabank_only: requisite.intrabank_only,
      timeVal: "sec",
    },
  });

  // const [min, sec] = watch(["min", "sec"]);

  // useEffect(() => {
  //   // const minNum = +min;
  //   // const secNum = +sec;
  //   let value = 0;
  //   if (!isNaN(minNum)) {
  //     value += minNum * 60;
  //   }
  //   if (!isNaN(secNum)) {
  //     value += secNum;
  //   }
  //   if (value != 0) setValue("min_interval", value);
  // }, [min, sec]);

  const { updateRequisite } = useRequisitesDispatch();

  const onSaveDetail = useCallback(
    async (data: UpdateRequisite) => {
      try {
        if (data.timeVal == "min" && data.min_interval) {
          data.min_interval = data.min_interval * 60;
        }
        await updateRequisite({ ...data, requisiteId: requisite.id });
        onEndEdit();
      } catch (error) {
        if (error instanceof JSONFetchClientError) {
          const match = error.data?.code?.match(errorCodeFieldRegex);
          const field = match ? (match[1] as keyof UpdateRequisite) : null;

          if (field) {
            setError(field, { type: "server", message: error.data.error });
          }
        }
      }
    },
    [onEndEdit, requisite.id, setError, updateRequisite]
  );

  return (
    <>
      <div className={styles.container}>
        <Breadcrumbs items={["Реквизиты", number!]} />
        <Block
          className={classNames(
            styles.detail,
            requisite.deleted && styles.disabled
          )}
        >
          <div className={styles.detailContainer}>
            <div className={styles.left}>
              <Card
                active={requisite.active}
                atWork={requisite.at_work}
                deleted={requisite.deleted}
                balance={requisite.balance}
                bankName={requisite.bank.name}
                scheduledActive={requisite.scheduled_active}
                name={requisite.full_name}
                number={number}
                className={styles.card}
                tooltip={requisite.scheduler_tooltip}
              />
              <div className={styles.inputsContainer}>
                <Input
                  control={control}
                  className={styles.input}
                  mask="+7 (999) 999-99-99"
                  name="phone_number"
                  label="Номер телефона"
                />
                <Input
                  control={control}
                  className={styles.input}
                  name="account_number"
                  label="Номер счета"
                />
                <Input
                  control={control}
                  className={styles.input}
                  name="card_number"
                  label="Номер карты"
                />
              </div>
            </div>
            <div className={styles.right}>
              <div className={styles.headerRight}>
                <h3>Редактирование</h3>
              </div>
              <div className={styles.rightBottom}>
                <DevicesSelect control={control} name="device_id" />
                <div />
                {(allowFarmCreation || isAdmin) && (
                  <>
                    <div className={styles.twoInputs}>
                      <p className={styles.title}>
                        Время работы реквизита <small>(UTC+3 МСК)</small>
                      </p>
                      <div className={styles.inputs}>
                        <Input
                          control={control}
                          name="start_time"
                          label="Начало"
                          className={styles.input}
                          mask="99:99"
                          placeholder="Начало"
                        />
                        <Input
                          control={control}
                          name="end_time"
                          label="Конец"
                          className={styles.input}
                          mask="99:99"
                          placeholder="Конец"
                        />
                      </div>
                    </div>
                    <div className={styles.twoInputs}>
                      <p className={styles.title}>Суммы сделок</p>
                      <div className={styles.inputs}>
                        <Input
                          control={control}
                          name="min_transaction"
                          label="Минимальная"
                          postNote="руб"
                          className={styles.input}
                          type="number"
                        />
                        <Input
                          control={control}
                          name="max_transaction"
                          label="Максимальная"
                          postNote="руб"
                          className={styles.input}
                          type="number"
                        />
                      </div>
                    </div>
                    <div className={styles.inputsContainer}>
                      <Input
                        control={control}
                        name="transaction_count"
                        label="Количество сделок за период"
                        postNote="шт"
                        className={styles.input}
                        type="number"
                      />
                      <Input
                        control={control}
                        name="max_amount_per_period"
                        type="number"
                        label="Максимальная сумма за период"
                        postNote="руб"
                        className={styles.input}
                        placeholder="Сумма"
                      />
                      <Checkbox
                        control={control}
                        name="intrabank_only"
                        label="Внутрибанковский режим (IBT)"
                        style={{ marginBottom: "10px" }}
                      />
                      <div className={styles.timeInterval}>
                        <label className={styles.label}>
                          Минимальный интервал между сделками
                        </label>
                        <div className={styles.flexdiv}>
                          <Input
                            control={control}
                            name="min_interval"
                            className={styles.input}
                            // label="Минимальный интервал между сделками"
                            // postNote="сек"
                            type="number"
                          />
                          <CustomSelect
                            name="timeVal"
                            control={control}
                            options={timeOptions}
                            // label="Минуты"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className={styles.inputsContainer}>
                  <Input
                    control={control}
                    className={styles.input}
                    name="full_name"
                    label="Имя реквизита"
                  />
                  <CustomSelect
                    name="type"
                    control={control}
                    label="Метод"
                    classNames={{
                      warpper: styles.selectWrapper,
                      error: styles.error,
                      label: styles.label,
                    }}
                    options={[
                      { text: "СБП", value: "SBP" },
                      { text: "Номер карты", value: "CARD" },
                      { text: "Номер счета", value: "ACCOUNT" },
                    ]}
                    placeholder="Метод"
                  />
                </div>
              </div>
            </div>
          </div>
          {!requisite.deleted && (
            <div className={styles.buttons}>
              <Button type="outline" onClick={onEndEdit}>
                Отменить изменения
              </Button>
              <Button onClick={handleSubmit(onSaveDetail)}>
                Сохранить изменения
              </Button>
            </div>
          )}
        </Block>
      </div>
      {isSubmitting && <GlobalLoader />}
    </>
  );
};

const timeOptions = [
  { text: "мин", value: "min" },
  { text: "сек", value: "sec" },
];
